import React from 'react'
import { useCallback, useState, useRef, useEffect, useMemo, RefObject } from 'react'
import { BackButton, LoginButton, NavBar } from '../../../../components/layout/NavBar'
import { Services } from '@/services/services'
import { useNavigate, useParams } from 'react-router-dom'

import './Dashboard.module.css'
import { Translates } from '@/services/repository/i18n'
import { CurrentSession } from '@/services/repository/session'
import { MainLayout, MainPageLayout } from '@/components/layout/MainLayout'
import { useLogin } from '@/context/login'
import { Machine } from '@/services/model/machine'
import { useModal } from '@/components/modal/Modal'
import { MachineFailureFormData, NewMachineFailureForm } from '@/components/forms/machines/Failures'
import { useForm } from 'react-hook-form'
import { addServerErrors } from '@/components/forms/common/FormBase'
import { MachinePath } from '../Common'
import { Roles } from '@/services/repository/users'
import { CommunalLangsButton } from '@/components/layout/Langs'
import { formatDate } from '@/common/time'

const rolesAllowsFailureView = [Roles.CompanyAdmin, Roles.Serviceman, Roles.SuperAdmin, Roles.HyperAdmin];

type UUID = string

interface FileInfo {
  id: string
  url: string
}

interface MachineFilesResp {
  id: UUID
  description: string
  files: FileInfo[]
  createdAt: string
  user: {
    username: string
  }
}

export interface NewMachineFailurePopupProps {
  services: Services,
  session?: CurrentSession,
  translates: Translates,
  isOpen: boolean,
  machineId: string | undefined
  onClose: () => void
}

export const NewMachineFailurePopup = ({ services, session, onClose, machineId, translates }: NewMachineFailurePopupProps) => {
  const form = useForm<MachineFailureFormData>()
  const navigate = useNavigate()
  const [message, setMessage] = useState<string | undefined>(undefined)

  const handle = (data: MachineFailureFormData): Promise<void> => {
    if (form === undefined) {
      return Promise.reject()
    }
    delete data.id
    return services.session.fetchWithToken(`/api/public/machines/byid/${machineId}/failures`, {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        machineFailureTypeId: parseInt(data.machineFailureTypeId as any)
      }),
    }).then(async (r) => {
      if (r.status !== 200) {
        const erros = await r.json()
        addServerErrors(form, translates, erros)
        return
      }
      const result = await r.json()
      form.reset()
      return result
    }).then((result) => {
      if (session && rolesAllowsFailureView.includes(session?.user.role as Roles)) {
        return navigate(`/communal/machine/${machineId}/failures`)
      }
      setMessage(translates.for(result?.status === 'pending' ? 'page.machinefailures.thanks_for_report_pending' : 'page.machinefailures.thanks_for_report'))
      // onClose()
    })
  }

  return <div>
    {message === undefined ? <NewMachineFailureForm
      onSubmit={handle}
      services={services}
      form={form}
      translates={translates}
      formName='machine_failures'
      readOnly={false}
      session={session}
    /> : <div className='communal-dashboard-message-popup'>{message}</div>}
  </div>
}

export const CommunalMachineFilesDashboard = ({ services, session }: { services: Services, session: CurrentSession | undefined }) => {
  const machineId = useParams().machineid
  const navigate = useNavigate()
  const [machine, setMachine] = useState<Machine>()
  const [machineFiles, setMachineFiles] = useState<MachineFilesResp[]>()
  const [translates, setTranslates] = useState<Translates>()

  useEffect(() => {
    if (session) {
      setTranslates(session.translates)
      return
    }
    services.i18n.get('main').then(setTranslates)
  }, [session])

  useEffect(() => {
    setMachine(undefined)
    fetch(`/api/communal/machines/byid/${machineId}`).then((response) => {
      return response.json()
    }).then((data) => {
      setMachine(data)
    })
  }, [machineId])

  useEffect(() => {
    services.session.fetchWithToken(`/api/communal/machines/byid/${machineId}/files`).then((response) => {
      return response.json()
    }).then((data) => {
      setMachineFiles(data)
      return data
    })
  }, [machineId])

  if (machine === undefined || translates === undefined || machineFiles === undefined) {
    return <div className='spinner'></div>
  }

  // if (machineFiles === undefined || translates === undefined) {
  //   return <div className='spinner'></div>
  // }

  return <MainPageLayout pageName='communal-dashboard-files' services={services} translates={translates} session={session} path={[...MachinePath({ session, translates, machine }), {
      title: translates.for(`page.communal-dashboard-files.title`)
    }]}
    navbarLeft={session === undefined ? <LoginButton translates={translates} /> : <BackButton services={services} translates={translates} fallbackUrl='/' />}
    navbarRight={<CommunalLangsButton session={session} services={services} translates={translates} />} >
    <div className="content-container">

      <a className='upload-btn' onClick={() => { navigate(`/communal/machine/${machineId}/files/upload`) }}>
        {translates.for('menus.communal.machine.upload_file')}
      </a>

      {machineFiles.map((row) => <div className='fileset-row'>
        <div className='header'>
          <div className='username'>{row.user.username}</div>
          <div className='created'>{formatDate(new Date(row.createdAt))}</div>
        </div>
        <div className='description'>{row.description}</div>
        <ul className='files'>
          {row.files ? row.files.map((file) => <li className='file-row'>
            <a href={file.url}>{file.id}</a>
          </li>) : null}
        </ul>
      </div>)}
    </div>
  </MainPageLayout>
}
